import { useCallback, useEffect, useState } from "react"

import Firebase from "#classes/Firebase"
import { GptHandler } from "#classes/GptHandler"
import { type QuickStoryScore } from "#customTypes/composedTypes/standoutTypes"
import {
  quickStoryNote,
  quickStoryScoreMatches,
  type QuickStoryScorePhase,
  quickStoryScorePhase,
  shouldRequestQuickStoryScore,
} from "#utils/quickStoryScore"

import type { FamilyReviewBucket } from "./standoutReviewHelpers"

const MAX_IN_FLIGHT = 2

let running = 0
const waiting: Array<() => void> = []

const pump = () => {
  while (running < MAX_IN_FLIGHT && waiting.length > 0) {
    const start = waiting.shift()
    if (!start) return
    running += 1
    start()
  }
}

const withSlot = <T>(fn: () => Promise<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    waiting.push(() => {
      fn()
        .then(resolve, reject)
        .finally(() => {
          running -= 1
          pump()
        })
    })
    pump()
  })

const jobs = new Map<string, Promise<void>>()

const ensureQuickStoryScore = (
  momentId: string,
  note: string
): Promise<void> => {
  const key = `${momentId}\0${note}`
  const existing = jobs.get(key)
  if (existing) return existing
  const job = withSlot(async () => {
    const result = await GptHandler.scoreQuickStory({ note })
    const stored: QuickStoryScore = {
      ...result,
      scoredAt: new Date().toISOString(),
      scoredNote: note,
    }
    // The note can change while this request is in flight, and a newer
    // score for the new text can finish first. Discard this result when
    // the moment no longer has the text that was scored.
    await Firebase.getInstance().writeQuickStoryScoreIfNoteMatches(
      momentId,
      note,
      stored
    )
  }).finally(() => {
    jobs.delete(key)
  })
  jobs.set(key, job)
  return job
}

export type { QuickStoryScorePhase } from "#utils/quickStoryScore"

export const useQuickStoryScore = (input: {
  momentId: string
  note?: string
  category?: string
  bucket: FamilyReviewBucket
  score?: QuickStoryScore
}): {
  phase: QuickStoryScorePhase
  score?: QuickStoryScore
  retry: () => void
} => {
  const note = quickStoryNote(input.note)
  const fresh = quickStoryScoreMatches(input.score, note)
  const request = shouldRequestQuickStoryScore({
    note,
    category: input.category,
    bucket: input.bucket,
    score: input.score,
  })
  const [attempt, setAttempt] = useState(0)
  const [failed, setFailed] = useState(false)
  const retry = useCallback(() => {
    setFailed(false)
    setAttempt((current) => current + 1)
  }, [])

  useEffect(() => {
    if (!request) return
    let active = true
    setFailed(false)
    void ensureQuickStoryScore(input.momentId, note).then(
      () => {
        if (active) setFailed(false)
      },
      (err: unknown) => {
        const detail = err instanceof Error ? err.message : String(err)
        console.error("[standout] quick story score failed", detail)
        if (active) setFailed(true)
      }
    )
    return () => {
      active = false
    }
  }, [attempt, input.momentId, note, request])

  const phase = quickStoryScorePhase({
    note,
    category: input.category,
    fresh,
    failed,
    request,
  })
  if (phase === "ready") return { phase, score: input.score, retry }
  return { phase, retry }
}
